import { createApp } from 'vue';
import {
  createWebHistory,
  createRouter,
} from 'vue-router';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import * as Sentry from '@sentry/vue';
import { genericRoutes } from './routes/genericRoutes.ts';
import { adminRoutes } from './routes/adminRoutes.ts';
import { authRoutes } from './routes/authRoutes.ts';
import { appRoutes } from './routes/appRoutes.ts';
import { useStore } from './api/index.ts';
import NavigationGuard from './routes/navigationGuard.ts';
import '@fortawesome/fontawesome-free/css/all.min.css';
import {
  Button,
  ConfigProvider,
  Spin,
  Result,
  Tag,
  Card,
  Empty,
  Skeleton,
  Radio,
  Tooltip,
  Affix,
  Menu,
  Dropdown,
  Row,
  Col,
  Form,
  Tabs,
  Upload,
  Select,
  Slider,
  Input,
  InputNumber,
  Breadcrumb,
  Table,
  Popover,
  Checkbox,
  Modal,
  Switch,
  List,
  Statistic,
  Typography,
  FloatButton,
  Space,
  Steps,
  Alert,
  Progress,
  Divider,
  TreeSelect,
  AutoComplete,
  Segmented,
  Tour,
  Pagination,
  DatePicker,
  Collapse,
} from 'ant-design-vue';
import PrimeVue from 'primevue/config';
import Aura from '@primeuix/themes/aura';
import InputOtp from 'primevue/inputotp';
import InstantSearch from 'vue-instantsearch/vue3/es';
import BasicLayout from './layouts/BasicLayout.vue';
import AdminLayout from './layouts/AdminLayout.vue';
import AuthLayout from './layouts/AuthLayout.vue';
import AppLayout from './layouts/AppLayout.vue';
import MarketingLayout from './layouts/MarketingLayout.vue';

// mounting point for the whole app
import App from './App.vue';
import VueKonva from 'vue-konva';
import type { State } from './api/types/index.ts';

// routes
const routes = [
  ...genericRoutes,
  ...appRoutes,
  ...authRoutes,
  ...adminRoutes,
  {
    path: '/:pathMatch(.*)*',
    component: () => import('./views/404ErrorView.vue'),
    meta: { layout: 'BasicLayout' },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(NavigationGuard);

// Error handler for cached chunks. Performs a full page reload if a chunk fails to load.
router.onError((error, to) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed')
  ) {
    if (!to?.fullPath) {
      window.location.reload();
    } else {
      window.location.href = to.fullPath;
    }
  }
});

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
pinia.use(Sentry.createSentryPiniaPlugin());
const app = createApp(App);

const vueApp = app.use(router)
  .use(pinia)
  .use(VueKonva)
  .use(PrimeVue, {
    theme: {
      preset: Aura,
      options: {
        darkModeSelector: false,
      },
    },
  })
  .use(ConfigProvider)
  .use(Button)
  .use(Typography)
  .use(Spin)
  .use(Result)
  .use(Tag)
  .use(Card)
  .use(Empty)
  .use(Radio)
  .use(Tooltip)
  .use(Affix)
  .use(Menu)
  .use(Dropdown)
  .use(Row)
  .use(Col)
  .use(Form)
  .use(Tabs)
  .use(Upload)
  .use(Select)
  .use(Slider)
  .use(Space)
  .use(Input)
  .use(InputNumber)
  .use(Breadcrumb)
  .use(Table)
  .use(Popover)
  .use(Checkbox)
  .use(Skeleton)
  .use(Modal)
  .use(Switch)
  .use(List)
  .use(Statistic)
  .use(FloatButton)
  .use(Steps)
  .use(Alert)
  .use(Progress)
  .use(InstantSearch)
  .use(Divider)
  .use(TreeSelect)
  .use(AutoComplete)
  .use(Segmented)
  .use(Tour)
  .use(Pagination)
  .use(DatePicker)
  .use(Collapse)
  .component('InputOtp', InputOtp)
  .component('BasicLayout', BasicLayout)
  .component('AdminLayout', AdminLayout)
  .component('AuthLayout', AuthLayout)
  .component('AppLayout', AppLayout)
  .component('MarketingLayout', MarketingLayout);

let colors: State['colors'];

try {
  // In case the store is not initialized yet
  colors = useStore().colors;
} catch {
  colors = {
    primary: {
      red: 16,
      green: 185,
      blue: 129,
    },
    loginBackground: {
      red: 30,
      green: 41,
      blue: 59,
    },
    teamId: null,
  };
}

// Only initialize Sentry if it hasn't been initialized already
try {
  Sentry.init({
    app,
    environment: import.meta.env.VITE_SENTRY_ENV,
    dsn: 'https://4d1597d7e25d4033a93321bc7aea2945@o4504731194097664.ingest.us.sentry.io/4504731198095360',
    integrations: [
      Sentry.feedbackIntegration({
        colorScheme: 'light',
        autoInject: false,
        showBranding: false,
        themeLight: {
          accentBackground: `rgb(${colors.primary.red}, ${colors.primary.green}, ${colors.primary.blue})`,
        },
      }),
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.replayCanvasIntegration(),
    ],
    tracePropagationTargets: [],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    // Don't replay sessions in development
    replaysOnErrorSampleRate: import.meta.env.VITE_SENTRY_ENV == 'production' ? 1 : 0,
  });
} catch (error) {
  // Sentry may already be initialized, ignore the error
  if (error instanceof Error && !error.message.includes('Multiple Sentry Session Replay instances')) {
    console.error('Sentry initialization error:', error);
  }
}

// Prevent double mounting - check if app is already mounted
const mountElement = document.getElementById('app');
if (mountElement && !(mountElement as any).__vue_app__) {
  vueApp.mount('#app');
} else if (mountElement) {
  console.warn('App is already mounted. Skipping remount.');
}

export { app, router };
